import React, { useState, useEffect } from 'react';
import { Box, Paper, Typography, Button, useTheme, Link, Chip } from '@mui/material';
import { EdvisorMessage as MessageType } from '../../services/edvisorService';
import { useEdvisor } from '../../contexts/EdvisorContext';
import { usePrograms } from '../../contexts/ProgramsContext';
import { useUser } from '../../contexts/UserContext';
import { usePrerequisites } from '../../contexts/PrerequisiteContext';
import { useSchools } from '../../contexts/SchoolsContext';
import { parseMessageContentAsHTML } from '../../utils/messageParser';
import EligibilityIcon, { EligibilityData } from '../shared/EligibilityIcon';
import { getProgramEligibilityFromFirebase, EligibilityResult } from '../../services/eligibilityCacheService';
import SuggestedPrompts from './SuggestedPrompts';
import { SuggestedPromptsService, SuggestedPrompt } from '../../services/suggestedPromptsService';
import './EdvisorMessage.css';

interface EdvisorMessageProps {
  message: MessageType;
  isTyping?: boolean;
}


const EdvisorMessage: React.FC<EdvisorMessageProps> = ({ message, isTyping = false }) => {
  const theme = useTheme();
  const { handleButtonClick, loading, messages } = useEdvisor();
  const { programs, getProgramEligibility, globalEligibilityCache } = usePrograms();
  const { schools } = useSchools();
  const { user } = useUser();
  const { prerequisites } = usePrerequisites();
  const isEdvisor = message.sender === 'edvisor';
  
  // Suggested prompts state
  const [suggestedPrompts, setSuggestedPrompts] = useState<SuggestedPrompt[]>([]);
  const suggestedPromptsService = new SuggestedPromptsService();
  const [previousThreadId, setPreviousThreadId] = useState<string | undefined>(message.threadId);

  // Derive user grade from courses
  const getUserGrade = () => {
    if (!user?.courses) return '10';
    
    const hasGrade12Courses = Object.values(user.courses).some((course: any) => 
      course.grade === '12' || course.grades === 12
    );
    const hasGrade11Courses = Object.values(user.courses).some((course: any) => 
      course.grade === '11' || course.grades === 11
    );
    
    if (hasGrade12Courses) return '12';
    if (hasGrade11Courses) return '11';
    return '10';
  };

  // State to track parsed HTML and force re-renders when eligibility data changes
  const [messageHTML, setMessageHTML] = useState('');
  const [messageElements, setMessageElements] = useState<React.ReactNode[]>([]);
  const [fetchedEligibilityData, setFetchedEligibilityData] = useState<{[programId: string]: EligibilityResult}>({});

  // Function to extract school names from Edvisor message content
  const extractSchoolNamesFromMessage = (content: string): string[] => {
    const schoolRegex = /!\[S\|([^|]+)\|([^\]]+)\]/g;
    const schoolNames: string[] = [];
    let match;
    
    while ((match = schoolRegex.exec(content)) !== null) {
      schoolNames.push(match[1].trim());
    }
    
    return schoolNames;
  };

  // Function to convert EligibilityResult to EligibilityData for EligibilityIcon component
  const convertEligibilityResult = (result: EligibilityResult): EligibilityData => {
    return {
      ifEligible: result.ifEligible,
      alertMessage: result.alertMessage,
      nextYearSuggestion: result.nextYearSuggestion,
      calculatedAverage: result.calculatedAverage,
      programAverage: result.programAverage,
      missingCourses: result.missingCourses,
      gradeIssues: result.gradeIssues,
      coursesUsedInAverage: result.coursesUsedInAverage
    };
  };

  // Function to fetch eligibility data from Firebase cache for missing programs
  const fetchMissingEligibilityData = async (programIds: string[]) => {
    if (!user?.uid || programIds.length === 0) return;
    
    try {
      console.log('🔄 EdvisorMessage: Fetching missing eligibility data from Firebase cache', { 
        programCount: programIds.length,
        programIds 
      });
      
      const newEligibilityData: {[programId: string]: EligibilityResult} = {};
      
      // Fetch eligibility data for each program in parallel
      const promises = programIds.map(async (programId) => {
        const eligibilityData = await getProgramEligibilityFromFirebase(user.uid, programId);
        if (eligibilityData) {
          newEligibilityData[programId] = eligibilityData;
        }
      });
      
      await Promise.all(promises);
      
      if (Object.keys(newEligibilityData).length > 0) {
        console.log('✅ EdvisorMessage: Fetched eligibility data from Firebase cache', { 
          fetched: Object.keys(newEligibilityData).length,
          total: programIds.length 
        });
        
        setFetchedEligibilityData(prev => ({
          ...prev,
          ...newEligibilityData
        }));
      }
    } catch (error) {
      console.error('❌ EdvisorMessage: Error fetching eligibility data from Firebase cache', error);
    }
  };

  // Function to parse message content with current eligibility data
  const parseMessageWithEligibility = () => {
    // Use ProgramsContext as primary source for eligibility data
    // This ensures we use the most current eligibility data with proper refresh triggers
    return parseMessageContentAsHTML(
      message.content,
      programs,
      getUserGrade(),
      user?.courses,
      prerequisites,
      schools
    );
  };

  // Function to replace React component placeholders with actual React components
  const replacePlaceholdersWithReactComponents = (html: string, programsData: any[]) => {
    const elements: React.ReactNode[] = [];
    
    console.log('🔍 Replacing placeholders in HTML:', html);
    console.log('🔍 Programs data:', programsData);
    console.log('🔍 Message allProgramsEligibility:', message.allProgramsEligibility);
    
    // Use matchAll to find all placeholders and their positions
    const placeholderRegex = /__REACT_ELIGIBILITY_ICON_([^_]+(?:_[^_]+)*)__/g;
    const matches = Array.from(html.matchAll(placeholderRegex));
    
    console.log('🔍 Found placeholders:', matches.length);
    
    let lastIndex = 0;
    
    matches.forEach((match, index) => {
      const fullMatch = match[0]; // The full placeholder string
      const programId = match[1]; // The captured program ID
      const matchIndex = match.index!;
      
      // Add HTML content before this placeholder
      if (matchIndex > lastIndex) {
        const htmlBefore = html.substring(lastIndex, matchIndex);
        if (htmlBefore.trim()) {
          elements.push(
            <span
              key={`html-${index}`}
              dangerouslySetInnerHTML={{ __html: htmlBefore }}
            />
          );
        }
      }
      
      // Find program data
      const programData = programsData?.find(p => p.id === programId);
      
      // Get eligibility data with enhanced fallback strategy:
      // 1. First try ProgramsContext (most current)
      // 2. Then try message.allProgramsEligibility (persisted in message)
      // 3. Then try fetchedEligibilityData (Firebase cache lookup)
      // 4. Finally try globalEligibilityCache (backup)
      let eligibilityData: EligibilityData | null = getProgramEligibility(programId);
      
      if (!eligibilityData && message.allProgramsEligibility?.[programId]) {
        console.log('🔍 Using eligibility from message.allProgramsEligibility for program:', programId);
        eligibilityData = message.allProgramsEligibility[programId];
      }
      
      if (!eligibilityData && fetchedEligibilityData[programId]) {
        console.log('🔍 Using eligibility from fetchedEligibilityData for program:', programId);
        eligibilityData = convertEligibilityResult(fetchedEligibilityData[programId]);
      }
      
      if (!eligibilityData && globalEligibilityCache[programId]) {
        console.log('🔍 Using eligibility from globalEligibilityCache for program:', programId);
        eligibilityData = globalEligibilityCache[programId];
      }
      
      console.log('🔍 Using eligibility for program:', programId, eligibilityData);
      
      // Create React component for eligibility icon
      elements.push(
        <EligibilityIcon
          key={`eligibility-${programId}-${index}`}
          eligibility={eligibilityData}
          userGrade={getUserGrade()}
          size={20}
        />
      );
      
      lastIndex = matchIndex + fullMatch.length;
    });
    
    // Add any remaining HTML after the last placeholder
    if (lastIndex < html.length) {
      const htmlAfter = html.substring(lastIndex);
      if (htmlAfter.trim()) {
        elements.push(
          <span
            key={`html-final`}
            dangerouslySetInnerHTML={{ __html: htmlAfter }}
          />
        );
      }
    }
    
    console.log('🔍 Final elements:', elements);
    return elements;
  };

  // Parse message HTML when component mounts or when eligibility data changes
  useEffect(() => {
    console.log('🔄 EdvisorMessage: useEffect triggered', {
      programsLength: programs.length,
      programsWithEligibility: programs.filter(p => p.eligibility).length,
      userCoursesChanged: !!user?.courses,
      prerequisitesChanged: !!prerequisites,
      schoolsLength: schools.length,
      hasAllProgramsEligibility: !!message.allProgramsEligibility,
      allProgramsEligibilityKeys: message.allProgramsEligibility ? Object.keys(message.allProgramsEligibility) : []
    });
    
    const html = parseMessageWithEligibility();
    setMessageHTML(html);
    
    // Use ProgramsContext as primary source for eligibility data
    // This ensures we use the most current eligibility data with proper refresh triggers
    const elements = replacePlaceholdersWithReactComponents(html, programs);
    setMessageElements(elements);
  }, [programs, user?.courses, prerequisites, schools]);

  // Additional effect to handle eligibility data loading after navigation
  useEffect(() => {
    // If we have message elements but no eligibility data yet, wait a bit and retry
    if (messageElements.length > 0 && programs.length > 0) {
      const hasAnyEligibilityData = programs.some(p => p.eligibility);
      const hasGlobalCache = Object.keys(globalEligibilityCache).length > 0;
      const hasMessageEligibility = message.allProgramsEligibility && Object.keys(message.allProgramsEligibility).length > 0;
      
      if (!hasAnyEligibilityData && !hasGlobalCache && !hasMessageEligibility) {
        console.log('🔄 EdvisorMessage: No eligibility data available yet, waiting for cache to load...');
        // Set a timeout to retry when eligibility data becomes available
        const timeout = setTimeout(() => {
          const html = parseMessageWithEligibility();
          const elements = replacePlaceholdersWithReactComponents(html, programs);
          setMessageElements(elements);
        }, 1000);
        
        return () => clearTimeout(timeout);
      }
    }
  }, [messageElements, programs, globalEligibilityCache, message.allProgramsEligibility]);

  // Effect to re-render when eligibility data becomes available
  useEffect(() => {
    // If we have message elements and eligibility data just became available, re-render
    if (messageElements.length > 0) {
      const hasAnyEligibilityData = programs.some(p => p.eligibility);
      const hasGlobalCache = Object.keys(globalEligibilityCache).length > 0;
      const hasMessageEligibility = message.allProgramsEligibility && Object.keys(message.allProgramsEligibility).length > 0;
      const hasFetchedEligibility = Object.keys(fetchedEligibilityData).length > 0;
      
      if (hasAnyEligibilityData || hasGlobalCache || hasMessageEligibility || hasFetchedEligibility) {
        console.log('🔄 EdvisorMessage: Eligibility data became available, re-rendering icons');
        const html = parseMessageWithEligibility();
        const elements = replacePlaceholdersWithReactComponents(html, programs);
        setMessageElements(elements);
      }
    }
  }, [programs, globalEligibilityCache, message.allProgramsEligibility, fetchedEligibilityData]);

  // Effect to fetch missing eligibility data from Firebase cache
  useEffect(() => {
    if (messageElements.length > 0 && user?.uid) {
      // Extract program IDs from message content
      const programRegex = /__REACT_ELIGIBILITY_ICON_([^_]+(?:_[^_]+)*)__/g;
      const matches = Array.from(messageHTML.matchAll(programRegex));
      const programIds = matches.map(match => match[1]);
      
      if (programIds.length > 0) {
        // Check which programs are missing eligibility data
        const missingProgramIds = programIds.filter(programId => {
          const hasProgramsContext = getProgramEligibility(programId);
          const hasMessageEligibility = message.allProgramsEligibility?.[programId];
          const hasFetchedEligibility = fetchedEligibilityData[programId];
          const hasGlobalCache = globalEligibilityCache[programId];
          
          return !hasProgramsContext && !hasMessageEligibility && !hasFetchedEligibility && !hasGlobalCache;
        });
        
        if (missingProgramIds.length > 0) {
          console.log('🔄 EdvisorMessage: Found missing eligibility data, fetching from Firebase cache', { 
            missing: missingProgramIds.length,
            total: programIds.length 
          });
          fetchMissingEligibilityData(missingProgramIds);
        }
      }
    }
  }, [messageElements, messageHTML, user?.uid, getProgramEligibility, message.allProgramsEligibility, fetchedEligibilityData, globalEligibilityCache]);

  // Reset memory when thread changes
  useEffect(() => {
    if (message.threadId !== previousThreadId) {
      suggestedPromptsService.resetMemory();
      setPreviousThreadId(message.threadId);
    }
  }, [message.threadId, previousThreadId]);

  // Generate suggested prompts for Edvisor messages
  useEffect(() => {
    if (isEdvisor && !isTyping && message.content) {
      const prompts = suggestedPromptsService.generatePrompts(message, []);
      setSuggestedPrompts(prompts);
    } else {
      setSuggestedPrompts([]);
    }
  }, [message, isTyping, isEdvisor]);

  // Initial parse when component mounts
  useEffect(() => {
    const html = parseMessageWithEligibility();
    setMessageHTML(html);
    
    // Use ProgramsContext as primary source for eligibility data
    // This ensures we use the most current eligibility data with proper refresh triggers
    const elements = replacePlaceholdersWithReactComponents(html, programs);
    setMessageElements(elements);
  }, []);

  // Note: Eligibility data is now managed by ProgramsContext cache
  // No need to trigger calculations here - the cache will provide the data

  // Set up global button click handler for HTML-injected buttons
  React.useEffect(() => {
    (window as any).handleButtonClick = (buttonLabel: string) => {
      handleButtonClick(buttonLabel);
    };


    // Set up global tour chip click handler
    (window as any).handleTourChipClick = (tourText: string) => {
      handleButtonClick(tourText);
    };

    return () => {
      delete (window as any).handleButtonClick;
      delete (window as any).handleTourChipClick;
    };
  }, [handleButtonClick]);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isEdvisor ? 'flex-start' : 'flex-end',
        mb: 2,
        alignItems: 'flex-start',
      }}
    >
      <Box sx={{ 
        maxWidth: '85%', 
        minWidth: { xs: '120px', sm: '200px' },
        width: isEdvisor ? '100%' : 'fit-content',
        wordWrap: 'break-word',
        overflowWrap: 'break-word',
        overflow: 'hidden'
      }}>
        <Paper
          elevation={1}
          sx={{
            p: 2,
            backgroundColor: isEdvisor 
              ? theme.palette.grey[100] 
              : theme.palette.primary.main,
            color: isEdvisor 
              ? theme.palette.text.primary 
              : theme.palette.primary.contrastText,
            borderRadius: 2,
            width: isEdvisor ? '100%' : 'fit-content',
            maxWidth: '100%',
            minWidth: 0,
          }}
        >
          {isTyping ? (
            <Box sx={{ display: 'flex', alignItems: 'center', minHeight: 24 }}>
              <Typography variant="body2" sx={{ mr: 1 }}>
                Ed is typing
              </Typography>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2].map((i) => (
                  <Box
                    key={i}
                    sx={{
                      width: 4,
                      height: 4,
                      borderRadius: '50%',
                      backgroundColor: 'currentColor',
                      animation: 'typing-dot 1.4s infinite ease-in-out',
                      animationDelay: `${i * 0.16}s`,
                      '@keyframes typing-dot': {
                        '0%, 80%, 100%': {
                          opacity: 0.3,
                          transform: 'scale(0.8)',
                        },
                        '40%': {
                          opacity: 1,
                          transform: 'scale(1)',
                        },
                      },
                    }}
                  />
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                wordWrap: 'break-word',
                overflowWrap: 'break-word',
                '& *': {
                  maxWidth: '100%',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
                '& .program-chip, & .school-chip, & .category-chip, & .event-chip, & .tour-chip': {
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
                '& .message-button': {
                  borderColor: isEdvisor ? theme.palette.primary.main : theme.palette.grey[400] + ' !important',
                  color: isEdvisor ? theme.palette.primary.main : theme.palette.text.primary + ' !important',
                  '&:hover': {
                    borderColor: isEdvisor ? theme.palette.primary.dark : theme.palette.grey[600] + ' !important',
                    backgroundColor: isEdvisor ? theme.palette.primary.light : theme.palette.grey[100] + ' !important',
                  },
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
                '& li': {
                  marginLeft: '16px',
                  marginBottom: '4px',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
                '& table': {
                  width: '100%',
                  tableLayout: 'fixed',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                },
                '& pre, & code': {
                  whiteSpace: 'pre-wrap',
                  wordWrap: 'break-word',
                  overflowWrap: 'break-word',
                  maxWidth: '100%',
                },
              }}
            >
              {(() => {
                console.log('🔍 Rendering decision - messageElements.length:', messageElements.length);
                console.log('🔍 messageElements:', messageElements);
                console.log('🔍 messageHTML length:', messageHTML.length);
                
                if (messageElements.length > 0) {
                  console.log('🔍 Using React elements for rendering');
                  return messageElements;
                } else {
                  console.log('🔍 Using HTML fallback for rendering');
                  return <span dangerouslySetInnerHTML={{ __html: messageHTML }} />;
                }
              })()}
              
              {/* Render suggested prompts */}
              <SuggestedPrompts 
                prompts={suggestedPrompts}
                onPromptClick={handleButtonClick}
                isEdvisor={isEdvisor}
              />
            </Box>
          )}
        </Paper>
      </Box>
    </Box>
  );
};

export default EdvisorMessage;
